import { env } from '@repo/env';
import { createClient, Provider, Session, User } from '@supabase/supabase-js';
import type { SupabaseAdapter, SupabaseError, ContactInquiryBase, ContactInquiry } from '@repo/shared';

// Create Supabase client
const client = createClient(env.supabase.url, env.supabase.anonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storage: typeof window !== 'undefined' ? window.localStorage : undefined,
  },
  global: {
    headers: { 'x-client-info': 'web-adapter' }
  }
});

// Table names
const TABLES = {
  CONTACT_INQUIRIES: 'contact_inquiries'
};

// Export web-specific Supabase adapter
export const supabaseAdapter: SupabaseAdapter = {
  client,
  async getUser() {
    const { data: { user } } = await client.auth.getUser();
    return user;
  },
  async signIn(email: string, password: string) {
    const { error } = await client.auth.signInWithPassword({ email, password });
    if (error) throw error;
  },
  async signUp(email: string, password: string) {
    const { error } = await client.auth.signUp({ email, password });
    if (error) throw error;
  },
  async signOut() {
    const { error } = await client.auth.signOut();
    if (error) throw error;
  },
  async resetPassword(email: string) {
    const { error } = await client.auth.resetPasswordForEmail(email);
    if (error) throw error;
  },
  async updatePassword(password: string) {
    const { error } = await client.auth.updateUser({ password });
    if (error) throw error;
  },
  async refreshSession() {
    const response = await client.auth.refreshSession();
    return response as { data: { session: Session | null }, error: SupabaseError | null };
  },
  async signInWithOAuth(params: { provider: Provider; options?: { redirectTo?: string } }) {
    return await client.auth.signInWithOAuth(params);
  },
  async verifyOtp(params: { token_hash: string; type: 'signup' | 'recovery' }) {
    const response = await client.auth.verifyOtp(params);
    return response as { data: { user: User | null; session: Session | null }, error: SupabaseError | null };
  },
  async resend(params: { type: 'signup' | 'email_change'; email: string }) {
    const { error } = await client.auth.resend(params);
    return { error: error as SupabaseError | null };
  },
  async exchangeCodeForSession(code: string) {
    const { error } = await client.auth.exchangeCodeForSession(code);
    return { error: error as SupabaseError | null };
  },
  
  // Contact inquiry methods
  async submitContactInquiry(data: ContactInquiryBase) {
    console.log('Submitting contact inquiry via adapter');
    let retries = 3;
    let delay = 500;
    
    for (let attempt = 1; attempt <= retries + 1; attempt++) {
      try {
        // Attempt to insert data into Supabase
        const { data: result, error } = await client
          .from(TABLES.CONTACT_INQUIRIES)
          .insert({
            ...data,
            status: 'new'
          })
          .select()
          .single();
        
        if (error) {
          console.error(`Contact inquiry submission error (attempt ${attempt}/${retries + 1}):`, error);
          
          // For most database errors, no need to retry
          return { data: null, error: error.message };
        }
        
        if (!result) {
          return { data: null, error: 'No data returned from database' };
        }
        
        console.log('Contact inquiry submitted successfully');
        return { data: result as ContactInquiry, error: null };
      } catch (error: any) {
        // Last attempt - don't retry
        if (attempt > retries) {
          return { 
            data: null, 
            error: error?.message || 'Unknown error submitting contact inquiry'
          };
        }
        
        // Check if this is a connection error that warrants a retry
        const shouldRetry = 
          (error?.message && (
            error.message.includes('fetch') || 
            error.message.includes('network') || 
            error.message.includes('timeout') ||
            error.message.includes('connection')
          ));
        
        if (!shouldRetry) {
          return { 
            data: null, 
            error: error?.message || 'Error submitting contact inquiry'
          };
        }
        
        console.log(`Database operation failed, retrying in ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
        
        // Increase delay for next attempt using exponential backoff
        delay *= 2;
      }
    }
    
    // This should never be reached due to the loop structure, but TypeScript needs it
    return { 
      data: null, 
      error: 'Failed to submit contact inquiry after multiple attempts'
    };
  }
}; 